import { useState, useEffect } from "react";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Trash2, Gauge, Car as CarIcon, PawPrint, Ban, CreditCard } from "lucide-react";
import { toast } from "sonner";
import LocationAutocomplete from "@/components/vendor/LocationAutocomplete";
import DistanceMatrix from "@/components/DistanceMatrix";
import InsurancePlansEditor from "@/components/InsurancePlansEditor";
import AddOnsEditor from "@/components/AddOnsEditor";
import AvailabilityEditor from "@/components/vendor/AvailabilityEditor";
import CarChannelSyncPanel from "@/components/vendor/CarChannelSyncPanel";
import VendorFeatureRequestCard from "@/components/vendor/VendorFeatureRequestCard";
import CarAlertsEditor from "@/components/vendor/CarAlertsEditor";
import CarRateRulesEditor from "@/components/vendor/CarRateRulesEditor";
import { CAR_MAKES, CAR_MODELS, OTHER_OPTION } from "@/data/carMakes";
import { FEATURE_CATALOG, FEATURE_TONE, matchFeature } from "@/components/car/featureCatalog";

const TYPES = ["Economy", "Compact", "Hatchback", "Sedan", "Wagon / Estate", "Coupe", "Convertible", "SUV", "SUV 4x4", "MPV / Family Vans", "Minivan", "Van", "Pickup Trucks", "Truck", "Luxury", "Electric & Hybrid"];
const TRANS = ["Automatic", "Manual"];
const FUEL = ["Gasoline", "Diesel", "Electric", "Hybrid", "LPG"];

export default function CarForm({ onSaved, car: existing = null }) {
  // Iter 67bm / Iter 69 — "Similar cars in the garage" multiplier.
  // Lets vendors add N identical copies in one shot (same specs, same images,
  // same insurance); each clone gets a unique id and an auto-suffixed model
  // number so license plates stay editable per copy from the My cars list.
  //
  // Semantics differ by mode:
  //   - Add mode: this is the TOTAL number of cars to list (1..50, default 1).
  //   - Edit mode: this is the number of ADDITIONAL copies to clone alongside
  //     the saved edits (0..50, default 0). Vendors realising they have 4
  //     identical cars instead of 1 can fix it without re-typing the form.
  const [duplicateCount, setDuplicateCount] = useState(existing ? 0 : 1);

  const isEdit = !!existing;
  // Iter 70 — local shadow of `existing` so children (e.g. VendorFeatureRequestCard)
  // can trigger a re-fetch of just the car without closing the whole modal.
  const [carShadow, setCarShadow] = useState(existing);
  useEffect(() => setCarShadow(existing), [existing]);
  const refetchCarShadow = async () => {
    if (!existing?.id) return;
    try {
      const r = await api.get(`/cars/${existing.id}`);
      setCarShadow(r.data);
    } catch {
      /* silent — child components will keep rendering the previous state */
    }
  };
  const [form, setForm] = useState(() => ({
    make: existing?.make ?? "",
    model: existing?.model ?? "",
    year: existing?.year ?? 2023,
    car_type: existing?.car_type ?? "Sedan",
    transmission: existing?.transmission ?? "Automatic",
    fuel_type: existing?.fuel_type ?? "Gasoline",
    seats: existing?.seats ?? 5,
    doors: existing?.doors ?? 4,
    luggage_capacity: existing?.luggage_capacity ?? "",
    engine_size: existing?.engine_size ?? "",
    fuel_policy: existing?.fuel_policy ?? "",
    mileage_policy: existing?.mileage_policy ?? "",
    mileage_limit_km_per_day: existing?.mileage_limit_km_per_day ?? 200,
    mileage_extra_km_rate: existing?.mileage_extra_km_rate ?? 0.30,
    minimum_rental_days: existing?.minimum_rental_days ?? "",
    maximum_rental_days: existing?.maximum_rental_days ?? "",
    minimum_driver_age: existing?.minimum_driver_age ?? "",
    late_return_grace_minutes: existing?.late_return_grace_minutes ?? 30,
    late_return_extra_day_fee: existing?.late_return_extra_day_fee ?? "",
    meet_greet_fee: existing?.meet_greet_fee ?? "",
    // Iter 67aw — free cancellation window. Stored in hours. Empty string ⇒
    // platform default at render time (48h). "0" ⇒ explicitly no free cancellation.
    free_cancellation_hours: existing?.free_cancellation_hours ?? "",
    custom_checklist_items: existing?.custom_checklist_items ?? [],
    // Iter 67bo — Pet & smoking permission toggles. Default `false` so the
    // chips render "Pets · No" / "Smoking · No" unless the vendor explicitly
    // opts in. Cars created before this iter have no field server-side; the
    // ?? falsy chain coerces to `false` for new listings and preserves the
    // server's boolean on edit.
    pet_friendly: existing?.pet_friendly ?? false,
    smoking_allowed: existing?.smoking_allowed ?? false,
    // Iter 67bp — Vendor-declared "no credit card required" badge surfaces on
    // browse cards. Default false because most vendors still pre-auth the
    // deposit on a card.
    no_credit_card_required: existing?.no_credit_card_required ?? false,
    price_per_day: existing?.price_per_day ?? 50,
    location: existing?.location ?? "",
    description: existing?.description ?? "",
    features: Array.isArray(existing?.features) ? existing.features.join(", ") : "",
    feature_keys: (() => {
      const keys = new Set();
      for (const raw of (existing?.features || [])) {
        const m = matchFeature(raw);
        if (m) keys.add(m.key);
      }
      return Array.from(keys);
    })(),
    features_custom: (() => {
      const custom = [];
      for (const raw of (existing?.features || [])) {
        if (!matchFeature(raw)) custom.push(raw);
      }
      return custom.join(", ");
    })(),
    images: existing?.images ?? [],
    pickup_locations: existing?.pickup_locations ?? [],
    dropoff_locations: existing?.dropoff_locations ?? [],
    one_way_fee: existing?.one_way_fee ?? 0,
    per_km_rate: existing?.per_km_rate ?? 0.50,
    distance_matrix: existing?.distance_matrix ?? {},
    deposit: existing?.deposit ?? 0,
    deposit_payment_method: existing?.deposit_payment_method ?? "cash_or_card",
    pickup_payment_methods: Array.isArray(existing?.pickup_payment_methods)
      ? existing.pickup_payment_methods
      : [],
    insurance_plans: (existing?.insurance_plans || []).map((p) => ({
      ...p,
      coverage: Array.isArray(p.coverage) ? p.coverage : [],
      // Iter 67bx — also normalise exclusions back to an array on load so the
      // editor's text field shows previously-saved values when re-opening a car.
      exclusions: Array.isArray(p.exclusions) ? p.exclusions : [],
    })),
    add_ons: (existing?.add_ons || []).map((a) => ({ ...a })),
    unavailable_ranges: (existing?.unavailable_ranges || []).map((r) => ({ ...r })),
    scheduled_pause_at: existing?.scheduled_pause_at || "",
    alerts: existing?.alerts ? { ...existing.alerts } : {},
  }));
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Fuel policy list comes from the backend so the source-of-truth is shared. Fallback to a
  // minimal local default if the metadata endpoint is unreachable (offline, network blip).
  const [fuelPolicies, setFuelPolicies] = useState([
    { key: "full_to_full", label: "Full to Full", description: "" },
    { key: "same_to_same", label: "Same to Same", description: "" },
    { key: "prepaid_full", label: "Pre-paid full tank", description: "" },
    { key: "free_tank",   label: "Free tank included", description: "" },
  ]);
  // Iter 67w — pick-up payment methods catalog (loaded from /api/meta).
  const [pickupMethodsCatalog, setPickupMethodsCatalog] = useState([
    { key: "cash",           label: "Cash at pickup" },
    { key: "card_on_pickup", label: "Card at pickup (POS)" },
    { key: "paypal",         label: "PayPal" },
    { key: "stripe",         label: "Stripe link (card)" },
    { key: "bank_transfer",  label: "Bank transfer" },
    { key: "revolut",        label: "Revolut" },
  ]);
  useEffect(() => {
    let cancelled = false;
    api.get("/meta/fuel-policies")
      .then((r) => { if (!cancelled && Array.isArray(r.data)) setFuelPolicies(r.data); })
      .catch(() => {});
    api.get("/meta/pickup-payment-methods")
      .then((r) => { if (!cancelled && Array.isArray(r.data)) setPickupMethodsCatalog(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const togglePickupMethod = (key) => {
    setForm((f) => {
      const set = new Set(f.pickup_payment_methods);
      if (set.has(key)) set.delete(key); else set.add(key);
      // Preserve catalog order so the chip strip is stable.
      const ordered = pickupMethodsCatalog
        .filter((m) => set.has(m.key))
        .map((m) => m.key);
      return { ...f, pickup_payment_methods: ordered };
    });
  };

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const paths = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        paths.push(data.path);
      }
      setForm((f) => ({ ...f, images: [...f.images, ...paths] }));
      toast.success(`Uploaded ${paths.length} image(s)`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const removeImage = (idx) => {
    setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));
  };

  const submit = async () => {
    setSaving(true);
    try {
      const pickup = form.pickup_locations.length ? form.pickup_locations : (form.location ? [form.location] : []);
      const dropoff = form.dropoff_locations.length ? form.dropoff_locations : (form.location ? [form.location] : []);
      const payload = {
        ...form,
        year: Number(form.year),
        seats: Number(form.seats),
        doors: Number(form.doors),
        luggage_capacity: Number(form.luggage_capacity) > 0 ? Number(form.luggage_capacity) : null,
        price_per_day: Number(form.price_per_day),
        one_way_fee: Number(form.one_way_fee) || 0,
        per_km_rate: Number(form.per_km_rate) || 0,
        distance_matrix: form.distance_matrix || {},
        deposit: Number(form.deposit) || 0,
        deposit_payment_method: form.deposit_payment_method || "cash_or_card",
        pickup_payment_methods: Array.isArray(form.pickup_payment_methods) ? form.pickup_payment_methods : [],
        // Mileage: 'limited' carries the two numeric companions; 'unlimited' and '' clear them.
        mileage_policy: form.mileage_policy || null,
        mileage_limit_km_per_day: form.mileage_policy === "limited" ? Number(form.mileage_limit_km_per_day) || null : null,
        mileage_extra_km_rate: form.mileage_policy === "limited" ? Number(form.mileage_extra_km_rate) || null : null,
        minimum_rental_days: Number(form.minimum_rental_days) > 0 ? Number(form.minimum_rental_days) : null,
        maximum_rental_days: Number(form.maximum_rental_days) > 0 ? Number(form.maximum_rental_days) : null,
        minimum_driver_age: Number(form.minimum_driver_age) > 0 ? Number(form.minimum_driver_age) : null,
        late_return_grace_minutes: form.late_return_grace_minutes !== "" && form.late_return_grace_minutes !== null
          ? Number(form.late_return_grace_minutes)
          : null,
        late_return_extra_day_fee: Number(form.late_return_extra_day_fee) > 0
          ? Number(form.late_return_extra_day_fee)
          : null,
        meet_greet_fee: Number(form.meet_greet_fee) > 0 ? Number(form.meet_greet_fee) : null,
        // Iter 67aw — Empty string ⇒ null (use platform default). Numeric 0 ⇒
        // persisted as 0 so the chip is intentionally hidden.
        free_cancellation_hours: form.free_cancellation_hours === "" || form.free_cancellation_hours === null
          ? null
          : Number(form.free_cancellation_hours),
        custom_checklist_items: (form.custom_checklist_items || []).filter((s) => (s || "").trim() !== ""),
        // Iter 67bo — pet & smoking toggles. Stored as proper booleans.
        pet_friendly: !!form.pet_friendly,
        smoking_allowed: !!form.smoking_allowed,
        // Iter 67bp — "No credit card required" badge toggle.
        no_credit_card_required: !!form.no_credit_card_required,
        features: (() => {
          const labels = (form.feature_keys || [])
            .map((k) => FEATURE_CATALOG.find((f) => f.key === k)?.label)
            .filter(Boolean);
          const custom = (form.features_custom || "")
            .split(",").map((s) => s.trim()).filter(Boolean);
          return [...labels, ...custom];
        })(),
        pickup_locations: pickup,
        dropoff_locations: dropoff,
        insurance_plans: (form.insurance_plans || [])
          .filter((p) => (p.name || "").trim())
          .map((p) => ({
            id: p.id,
            name: (p.name || "").trim(),
            price_per_day: Number(p.price_per_day) || 0,
            description: (p.description || "").trim(),
            coverage: Array.isArray(p.coverage)
              ? p.coverage
              : (p.coverage || "").split(",").map((s) => s.trim()).filter(Boolean),
            // Iter 67bu/bx — preserve exclusions, deposit, excess. Backend
            // accepts either a CSV string or an array (field_validator on
            // InsurancePlan splits commas/newlines), but we normalise here so
            // the wire payload is consistently shaped and matches `coverage`.
            exclusions: Array.isArray(p.exclusions)
              ? p.exclusions
              : (p.exclusions || "").split(",").map((s) => s.trim()).filter(Boolean),
            deposit: Number(p.deposit) || 0,
            excess: Number(p.excess) || 0,
          })),
      };
      if (isEdit) {
        await api.patch(`/cars/${existing.id}`, payload);
        // Iter 69 — also clone N additional copies if the vendor bumped the
        // "Similar cars in the garage" counter while editing. Same numbering
        // scheme as Add mode: first clone is `#2`, plate `TEMP-2`, etc.
        const extra = Math.max(0, Math.min(50, Number(duplicateCount) || 0));
        for (let i = 0; i < extra; i++) {
          const clonePayload = { ...payload };
          clonePayload.model = `${payload.model} #${i + 2}`;
          clonePayload.plate_number = `TEMP-${i + 2}`;
          await api.post("/cars", clonePayload);
        }
        if (extra === 0) {
          toast.success("Car updated");
        } else {
          toast.success(`Car updated · ${extra} additional ${extra === 1 ? "copy" : "copies"} listed — edit plates from My cars`);
        }
      } else {
        // Iter 67bm/67bn — when "Similar cars" > 1, POST the same payload N
        // times. Each clone gets a `#k` suffix on its model and a `TEMP-{k}`
        // license plate so vendors can spot the unfinished entries at a glance
        // in My cars (and edit plates afterwards). Original (k=1) is left
        // alone so vendors who only want one car see the model name as typed.
        const count = Math.max(1, Math.min(50, Number(duplicateCount) || 1));
        for (let i = 0; i < count; i++) {
          const clonePayload = { ...payload };
          if (count > 1 && i > 0) {
            clonePayload.model = `${payload.model} #${i + 1}`;
            clonePayload.plate_number = `TEMP-${i + 1}`;
          }
          await api.post("/cars", clonePayload);
        }
        if (count === 1) {
          toast.success("Car listed");
        } else {
          toast.success(`${count} similar cars listed — edit license plates from My cars`);
        }
      }
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: typeof v === "object" && v?.target ? v.target.value : v }));

  return (
    <div className="space-y-4 min-w-0">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <MakePicker make={form.make} onChange={(v) => setForm((f) => ({ ...f, make: v, model: "" }))} />
        <ModelPicker make={form.make} model={form.model} onChange={(v) => setForm((f) => ({ ...f, model: v }))} />
        <div><Label>Year</Label><Input type="number" className="mt-1 rounded-xl" value={form.year} onChange={set("year")} data-testid="car-year" /></div>
        <div>
          <Label>Type</Label>
          <Select value={form.car_type} onValueChange={set("car_type")}>
            <SelectTrigger className="mt-1 rounded-xl" data-testid="car-type"><SelectValue /></SelectTrigger>
            <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Transmission</Label>
          <Select value={form.transmission} onValueChange={set("transmission")}>
            <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent>{TRANS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Fuel</Label>
          <Select value={form.fuel_type} onValueChange={set("fuel_type")}>
            <SelectTrigger className="mt-1 rounded-xl" data-testid="car-fuel"><SelectValue /></SelectTrigger>
            <SelectContent>{FUEL.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Seats</Label><Input type="number" className="mt-1 rounded-xl" value={form.seats} onChange={set("seats")} /></div>
        <div><Label>Doors</Label><Input type="number" className="mt-1 rounded-xl" value={form.doors} onChange={set("doors")} /></div>
        <div>
          <Label>Suitcases</Label>
          <Input
            type="number"
            min="0"
            max="20"
            className="mt-1 rounded-xl"
            value={form.luggage_capacity}
            onChange={set("luggage_capacity")}
            placeholder="e.g. 3"
            data-testid="car-luggage-capacity"
          />
        </div>
        <div>
          <Label>Meet & Greet airport fee</Label>
          <Input
            type="number"
            min="0"
            step="0.5"
            className="mt-1 rounded-xl"
            value={form.meet_greet_fee}
            onChange={set("meet_greet_fee")}
            placeholder="e.g. 15"
            data-testid="car-meet-greet-fee"
          />
          <div className="text-[10px] text-neutral-500 mt-1">Auto-applied when renter selects an airport pickup. Leave blank to disable.</div>
        </div>
        <div>
          <Label>Free cancellation window</Label>
          <Select
            value={form.free_cancellation_hours === "" || form.free_cancellation_hours === null ? "default" : String(form.free_cancellation_hours)}
            onValueChange={(v) => setForm((f) => ({ ...f, free_cancellation_hours: v === "default" ? "" : Number(v) }))}
          >
            <SelectTrigger className="mt-1 rounded-xl" data-testid="car-free-cancellation-window">
              <SelectValue placeholder="Use platform default (48h)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="default">Platform default · 48h</SelectItem>
              <SelectItem value="24">24 hours before pickup</SelectItem>
              <SelectItem value="48">48 hours before pickup</SelectItem>
              <SelectItem value="72">72 hours before pickup</SelectItem>
              <SelectItem value="168">7 days before pickup</SelectItem>
              <SelectItem value="336">14 days before pickup</SelectItem>
              <SelectItem value="0">No free cancellation</SelectItem>
            </SelectContent>
          </Select>
          <div className="text-[10px] text-neutral-500 mt-1">Surfaces as a "Free cancellation X" chip on car cards and on the public car page.</div>
        </div>
        <div>
          <Label>Engine size</Label>
          <Input
            className="mt-1 rounded-xl"
            value={form.engine_size}
            onChange={set("engine_size")}
            placeholder="e.g. 2.0L, 1998cc, 75 kWh"
            data-testid="car-engine-size"
          />
        </div>
        <div>
          <Label>Fuel policy</Label>
          <Select value={form.fuel_policy || ""} onValueChange={(v) => setForm((f) => ({ ...f, fuel_policy: v === "__clear__" ? "" : v }))}>
            <SelectTrigger className="mt-1 rounded-xl" data-testid="car-fuel-policy"><SelectValue placeholder="Pick a policy" /></SelectTrigger>
            <SelectContent>
              {fuelPolicies.map((p) => (
                <SelectItem key={p.key} value={p.key} data-testid={`fuel-policy-${p.key}`}>
                  {p.label}
                </SelectItem>
              ))}
              {form.fuel_policy && <SelectItem value="__clear__" data-testid="fuel-policy-clear">— Clear —</SelectItem>}
            </SelectContent>
          </Select>
          {form.fuel_policy && (
            <p className="text-xs text-neutral-500 mt-1">
              {fuelPolicies.find((p) => p.key === form.fuel_policy)?.description}
            </p>
          )}
        </div>
        <div><Label>Price / day (€)</Label><Input type="number" className="mt-1 rounded-xl" value={form.price_per_day} onChange={set("price_per_day")} data-testid="car-price" /></div>
        <div>
          <Label>Location</Label>
          <p className="text-[11px] text-neutral-500 mt-1 mb-1">Pick the main city or airport from the platform catalog — used as a fallback when no specific pickup is chosen at checkout.</p>
          <LocationAutocomplete
            mode="single"
            value={form.location}
            onChange={(v) => set("location")(v)}
            placeholder="Search city, airport or port…"
            testIdPrefix="car-location"
          />
        </div>
        <div>
          <Label>Minimum rental days</Label>
          <Input
            type="number"
            min="0"
            step="1"
            className="mt-1 rounded-xl"
            value={form.minimum_rental_days}
            onChange={set("minimum_rental_days")}
            placeholder="0 = no minimum"
            data-testid="car-min-rental-days"
          />
          <p className="text-[11px] text-neutral-500 mt-1">Renters can't book this car for fewer days than this. Leave blank or 0 for no minimum.</p>
        </div>
        <div>
          <Label>Maximum rental days</Label>
          <Input
            type="number"
            min="0"
            step="1"
            className="mt-1 rounded-xl"
            value={form.maximum_rental_days}
            onChange={set("maximum_rental_days")}
            placeholder="0 = no cap"
            data-testid="car-max-rental-days"
          />
          <p className="text-[11px] text-neutral-500 mt-1">Longer rentals will be blocked and the renter is invited to message you for a custom quote. Leave blank or 0 for no cap.</p>
        </div>
        <div>
          <Label>Minimum driver age (years)</Label>
          <Input
            type="number"
            min="0"
            max="80"
            step="1"
            className="mt-1 rounded-xl"
            value={form.minimum_driver_age}
            onChange={set("minimum_driver_age")}
            placeholder="0 = no minimum (e.g. 21, 25)"
            data-testid="car-min-driver-age"
          />
          <p className="text-[11px] text-neutral-500 mt-1">Renters see this requirement on the listing. Verify the driver's ID at handover.</p>
        </div>
        <div>
          <Label>Late-return grace (minutes)</Label>
          <Input
            type="number"
            min="0"
            step="5"
            className="mt-1 rounded-xl"
            value={form.late_return_grace_minutes}
            onChange={set("late_return_grace_minutes")}
            placeholder="30"
            data-testid="car-late-return-grace"
          />
          <p className="text-[11px] text-neutral-500 mt-1">Buffer before a late return triggers an extra-day charge. Default 30 min.</p>
        </div>
        <div>
          <Label>Late-return extra-day fee (€)</Label>
          <Input
            type="number"
            min="0"
            step="0.01"
            className="mt-1 rounded-xl"
            value={form.late_return_extra_day_fee}
            onChange={set("late_return_extra_day_fee")}
            placeholder="Defaults to the daily price"
            data-testid="car-late-return-fee"
          />
          <p className="text-[11px] text-neutral-500 mt-1">Charged per extra day after grace. Leave blank to use the regular daily price.</p>
        </div>
      </div>
      <div><Label>Description</Label><Textarea className="mt-1 rounded-xl" value={form.description} onChange={set("description")} rows={3} data-testid="car-description" /></div>
      <div className="rounded-2xl border border-border p-4 bg-gradient-to-br from-sky-50/30 via-white to-violet-50/30" data-testid="car-features-section">
        <Label className="flex items-center gap-2">
          Vehicle features
          <span className="text-[10px] uppercase tracking-wider font-bold bg-sky-100 text-sky-700 border border-sky-200 px-1.5 py-0.5 rounded-full">
            {(form.feature_keys || []).length} selected
          </span>
        </Label>
        <p className="text-xs text-neutral-500 mt-1 mb-3">
          Tick every amenity this car has. Surfaced as colour-coded chips on the public car page, browse cards, and the rental agreement.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="car-features-grid">
          {FEATURE_CATALOG.map((f) => {
            const selected = (form.feature_keys || []).includes(f.key);
            const tone = FEATURE_TONE[f.key];
            const Icon = f.icon;
            return (
              <button
                key={f.key}
                type="button"
                aria-pressed={selected}
                onClick={() => setForm((s) => {
                  const cur = new Set(s.feature_keys || []);
                  if (cur.has(f.key)) cur.delete(f.key); else cur.add(f.key);
                  // Persist in catalog order so the saved list is stable.
                  const ordered = FEATURE_CATALOG.filter((c) => cur.has(c.key)).map((c) => c.key);
                  return { ...s, feature_keys: ordered };
                })}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl border-2 text-xs font-semibold transition text-left ${
                  selected
                    ? `${tone.bg} ${tone.fg} border-current shadow-sm`
                    : "bg-white text-neutral-600 border-border hover:border-neutral-300 hover:bg-neutral-50/50"
                }`}
                data-testid={`car-feature-${f.key}`}
              >
                <span className={`h-7 w-7 rounded-lg ${selected ? "bg-white/70" : tone.bg} ring-1 ${tone.ring} flex items-center justify-center shrink-0`}>
                  <Icon className={`h-3.5 w-3.5 ${tone.fg}`} />
                </span>
                <span className="leading-tight">{f.label}</span>
              </button>
            );
          })}
        </div>
        <div className="mt-4">
          <Label className="text-xs">Additional features (comma-separated)</Label>
          <Input
            className="mt-1 rounded-xl"
            value={form.features_custom}
            onChange={(e) => setForm((s) => ({ ...s, features_custom: e.target.value }))}
            placeholder="e.g. Apple CarPlay, Roof rack, Tow bar"
            data-testid="car-features-custom"
          />
          <p className="text-[11px] text-neutral-500 mt-1">Anything not in the list above — these render as neutral chips on the public page.</p>
        </div>
        {/* Iter 67bo — Pet & smoking permissions. These render as cyan/emerald
            and red chips on the public spec strip; vendors can toggle either
            without leaving the car form. */}
        <div className="mt-5 pt-4 border-t border-border" data-testid="car-permissions-block">
          <Label className="text-sm font-semibold">Renter permissions</Label>
          <p className="text-[11px] text-neutral-500 mt-1 mb-3">Surfaced prominently on the car detail page — pre-empts &quot;can I bring my dog?&quot; / &quot;can I smoke?&quot; support tickets.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            <button
              type="button"
              aria-pressed={!!form.pet_friendly}
              onClick={() => setForm((s) => ({ ...s, pet_friendly: !s.pet_friendly }))}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border-2 text-xs font-semibold transition text-left ${
                form.pet_friendly
                  ? "bg-emerald-50 text-emerald-800 border-emerald-300 shadow-sm"
                  : "bg-white text-neutral-600 border-border hover:border-neutral-300"
              }`}
              data-testid="car-pet-friendly-toggle"
            >
              <span className={`h-7 w-7 rounded-lg ${form.pet_friendly ? "bg-white/70" : "bg-emerald-50"} ring-1 ring-emerald-200 flex items-center justify-center shrink-0`}>
                <PawPrint className={`h-3.5 w-3.5 text-emerald-600`} />
              </span>
              <span className="leading-tight">
                Pet-friendly
                <div className="text-[10px] font-normal opacity-70 mt-0.5">{form.pet_friendly ? "Pets are welcome" : "No pets allowed"}</div>
              </span>
            </button>
            <button
              type="button"
              aria-pressed={!!form.smoking_allowed}
              onClick={() => setForm((s) => ({ ...s, smoking_allowed: !s.smoking_allowed }))}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border-2 text-xs font-semibold transition text-left ${
                form.smoking_allowed
                  ? "bg-amber-50 text-amber-800 border-amber-300 shadow-sm"
                  : "bg-rose-50 text-rose-800 border-rose-200"
              }`}
              data-testid="car-smoking-toggle"
            >
              <span className={`h-7 w-7 rounded-lg ${form.smoking_allowed ? "bg-white/70" : "bg-white/70"} ring-1 ${form.smoking_allowed ? "ring-amber-200" : "ring-rose-200"} flex items-center justify-center shrink-0`}>
                <Ban className={`h-3.5 w-3.5 ${form.smoking_allowed ? "text-amber-600" : "text-rose-600"}`} />
              </span>
              <span className="leading-tight">
                Smoking
                <div className="text-[10px] font-normal opacity-70 mt-0.5">{form.smoking_allowed ? "Permitted in this car" : "Strictly not allowed"}</div>
              </span>
            </button>
            {/* Iter 67bp — Vendor-declared "no credit card required" toggle.
                Surfaces an amber badge on the public browse card so renters
                without a credit card can spot eligible vehicles instantly. */}
            <button
              type="button"
              aria-pressed={!!form.no_credit_card_required}
              onClick={() => setForm((s) => ({ ...s, no_credit_card_required: !s.no_credit_card_required }))}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border-2 text-xs font-semibold transition text-left ${
                form.no_credit_card_required
                  ? "bg-amber-50 text-amber-800 border-amber-300 shadow-sm"
                  : "bg-white text-neutral-600 border-border hover:border-neutral-300"
              }`}
              data-testid="car-no-credit-card-toggle"
            >
              <span className="relative h-7 w-7 rounded-lg bg-white/80 ring-1 ring-amber-200 flex items-center justify-center shrink-0">
                <CreditCard className="h-3.5 w-3.5 text-amber-600" />
                {/* Diagonal slash overlay — visual "no" without an extra icon. */}
                <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <span className="block h-[1.5px] w-5 bg-rose-500 rotate-45 origin-center" />
                </span>
              </span>
              <span className="leading-tight">
                No credit card
                <div className="text-[10px] font-normal opacity-70 mt-0.5">{form.no_credit_card_required ? "Cash deposit accepted" : "Card required at pickup"}</div>
              </span>
            </button>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
        <div>
          <Label>Pickup destinations</Label>
          <p className="text-xs text-neutral-500 mt-1 mb-2">Pick from the platform catalog — renters can only collect the car at curated, autocomplete-matched locations. Defaults to the main location if left empty.</p>
          <LocationAutocomplete
            mode="multi"
            value={form.pickup_locations}
            onChange={(v) => set("pickup_locations")(v)}
            placeholder="Search a pickup city, airport or port…"
            testIdPrefix="pickup"
          />
        </div>
        <div>
          <Label>Drop-off destinations</Label>
          <p className="text-xs text-neutral-500 mt-1 mb-2">Can match pickup or differ for one-way rentals. Same catalog — no free text.</p>
          <LocationAutocomplete
            mode="multi"
            value={form.dropoff_locations}
            onChange={(v) => set("dropoff_locations")(v)}
            placeholder="Search a drop-off city, airport or port…"
            testIdPrefix="dropoff"
          />
        </div>
      </div>
      <div>
        <Label>One-way fee fallback (€)</Label>
        <p className="text-xs text-neutral-500 mt-1 mb-2">Used only when a route isn't covered by the distance matrix below. Leave at 0 to disable.</p>
        <Input type="number" min="0" step="1" className="rounded-xl max-w-xs" value={form.one_way_fee} onChange={set("one_way_fee")} data-testid="car-one-way-fee" />
      </div>
      <div className="rounded-2xl border border-border p-4 space-y-3 bg-neutral-50/40">
        <div>
          <Label>Per-km rate (€)</Label>
          <p className="text-xs text-neutral-500 mt-1 mb-2">e.g. <span className="font-medium text-neutral-700">0.50</span> means a 100 km route costs €50 in drop-off fee. Set to 0 to use only the flat fallback above.</p>
          <Input type="number" min="0" step="0.01" className="rounded-xl max-w-xs" value={form.per_km_rate} onChange={set("per_km_rate")} data-testid="car-per-km-rate" />
        </div>
        <DistanceMatrix
          pickups={form.pickup_locations.length ? form.pickup_locations : (form.location ? [form.location] : [])}
          dropoffs={form.dropoff_locations.length ? form.dropoff_locations : (form.location ? [form.location] : [])}
          value={form.distance_matrix}
          onChange={(v) => set("distance_matrix")(v)}
        />
      </div>
      <InsurancePlansEditor
        value={form.insurance_plans}
        onChange={(v) => set("insurance_plans")(v)}
      />
      <AddOnsEditor
        value={form.add_ons}
        onChange={(v) => set("add_ons")(v)}
      />
      <AvailabilityEditor
        unavailableRanges={form.unavailable_ranges}
        scheduledPauseAt={form.scheduled_pause_at}
        onChangeRanges={(v) => set("unavailable_ranges")(v)}
        onChangeScheduledPause={(v) => set("scheduled_pause_at")(v)}
      />
      {/* Iter 67ef — Per-car iCal channel sync (export to OTAs + import from
          them). Only shown in edit mode because we need a saved car id to
          mint the read-only export token. */}
      {isEdit && existing?.id && (
        <CarChannelSyncPanel carId={existing.id} />
      )}
      <CarAlertsEditor
        value={form.alerts}
        onChange={(v) => set("alerts")(v)}
      />
      <MileagePolicyEditor
        policy={form.mileage_policy}
        limit={form.mileage_limit_km_per_day}
        rate={form.mileage_extra_km_rate}
        onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
      />
      <CustomChecklistEditor
        items={form.custom_checklist_items}
        onChange={(items) => setForm((f) => ({ ...f, custom_checklist_items: items }))}
      />
      <div className="rounded-2xl border border-border p-4 bg-neutral-50/40">
        <Label data-testid="car-deposit-label">Minimum-coverage deposit (€)</Label>
        <p className="text-xs text-neutral-500 mt-1 mb-2">
          Charged at handover when the renter <strong>does not pick an insurance plan</strong> (i.e. they're on the baseline minimum coverage). Each insurance plan above has its own deposit override — leave 0 there for "free deposit" upsells. Set this to 0 to skip entirely.
        </p>
        <Input type="number" min="0" step="50" className="rounded-xl max-w-xs" value={form.deposit} onChange={set("deposit")} data-testid="car-deposit" />
        <div className="mt-4">
          <Label>Deposit payment method</Label>
          <p className="text-xs text-neutral-500 mt-1 mb-2">How the renter pays the deposit at handover — shown on the public car page and on the booking summary.</p>
          <div className="flex flex-wrap gap-2" data-testid="car-deposit-method">
            {[
              { value: "cash", label: "Cash only" },
              { value: "card", label: "Credit / debit card only" },
              { value: "cash_or_card", label: "Cash or card" },
            ].map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setForm((f) => ({ ...f, deposit_payment_method: opt.value }))}
                className={`rounded-full px-4 py-2 text-sm border transition ${form.deposit_payment_method === opt.value ? "bg-blue-600 text-white border-blue-600" : "bg-white border-border hover:bg-neutral-50"}`}
                data-testid={`car-deposit-method-${opt.value}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-5 pt-4 border-t border-dashed border-border" data-testid="car-pickup-payment-methods-section">
          <Label className="flex items-center gap-2">
            Payment methods accepted at pick-up
            <span className="text-[10px] uppercase tracking-wider font-bold bg-indigo-50 text-indigo-700 border border-indigo-200 px-1.5 py-0.5 rounded-full">For rental balance</span>
          </Label>
          <p className="text-xs text-neutral-500 mt-1 mb-3">
            Tick every method you'll accept when the renter settles the remaining rental balance at vehicle handover.
            Shown on the public car page, during checkout, in the confirmation email, and on the rental agreement / invoice PDFs.
            <span className="block mt-1 text-[11px] text-neutral-400 italic">Leave all unchecked to inherit your vendor-level defaults.</span>
          </p>
          <div className="flex flex-wrap gap-2" data-testid="car-pickup-payment-methods">
            {pickupMethodsCatalog.map((opt) => {
              const checked = (form.pickup_payment_methods || []).includes(opt.key);
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => togglePickupMethod(opt.key)}
                  aria-pressed={checked}
                  className={`rounded-full px-3.5 py-1.5 text-xs font-semibold border-2 transition ${
                    checked
                      ? "bg-gradient-to-r from-emerald-500 to-teal-600 text-white border-emerald-600 shadow-sm shadow-emerald-200"
                      : "bg-white text-neutral-700 border-border hover:border-emerald-300 hover:bg-emerald-50/40"
                  }`}
                  data-testid={`car-pickup-payment-${opt.key}`}
                >
                  {checked ? "✓ " : ""}{opt.label}
                </button>
              );
            })}
          </div>
          {(form.pickup_payment_methods || []).length > 0 && (
            <div className="mt-2 text-[11px] text-emerald-700 font-medium">
              {form.pickup_payment_methods.length} method{form.pickup_payment_methods.length === 1 ? "" : "s"} selected — these will be displayed publicly on this car.
            </div>
          )}
        </div>
      </div>
      <div>
        <Label>Photos</Label>
        <div className="mt-2 flex flex-wrap gap-2">
          {form.images.map((p, i) => {
            const src = p.startsWith("http") ? p : fileUrl(p);
            return (
              <div key={`${p}-${i}`} className="relative group">
                <img src={src} alt="" className="h-20 w-20 rounded-xl object-cover" />
                <button
                  type="button"
                  onClick={() => removeImage(i)}
                  className="absolute -top-1.5 -right-1.5 h-6 w-6 rounded-full bg-neutral-900 text-white flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition"
                  data-testid={`remove-image-${i}`}
                  aria-label="Remove photo"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            );
          })}
          <label className="h-20 w-20 rounded-xl border-2 border-dashed border-border flex items-center justify-center cursor-pointer hover:bg-neutral-50">
            <input type="file" accept="image/*" multiple className="hidden" onChange={upload} disabled={uploading} data-testid="car-upload" />
            <Upload className="h-5 w-5 text-neutral-500" />
          </label>
        </div>
      </div>
      {isEdit && existing?.id && (
        <div className="pt-2 border-t border-border">
          <CarRateRulesEditor
            carId={existing.id}
            basePricePerDay={form.price_per_day}
            carImage={(form.images && form.images[0]) || (existing.images && existing.images[0]) || null}
            carName={[existing.make, existing.model, existing.year].filter(Boolean).join(" ")}
          />
        </div>
      )}
      {/* Iter 69 — visible on both Add and Edit. The wording flips between
          "How many cars to list" (Add: 1..50) and "How many additional copies"
          (Edit: 0..50). */}
      <div className="pt-3 border-t border-border" data-testid="similar-cars-block">
          <div className="rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50/60 to-white p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="h-9 w-9 rounded-xl bg-violet-100 text-violet-700 flex items-center justify-center ring-1 ring-violet-200">
                <CarIcon className="h-4 w-4" />
              </div>
              <div>
                <Label className="text-base font-semibold text-violet-900">Similar cars in the garage</Label>
                <div className="text-xs text-neutral-600 mt-0.5">
                  {isEdit
                    ? "Realised you have more identical vehicles than expected? Clone this car a few more times in one go — you can tweak license plates afterwards from My cars."
                    : "Got multiple identical vehicles (same make, model, year, specs)? Add them all in one go — you can tweak license plates afterwards from My cars."}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 mt-3">
              <Label className="text-sm font-semibold text-neutral-700 whitespace-nowrap" htmlFor="similar-cars-count">
                {isEdit ? "How many additional copies" : "How many cars to list"}
              </Label>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 rounded-lg"
                  onClick={() => setDuplicateCount((v) => Math.max(isEdit ? 0 : 1, Number(v || 0) - 1))}
                  disabled={Number(duplicateCount) <= (isEdit ? 0 : 1)}
                  data-testid="similar-cars-decrement"
                >−</Button>
                <Input
                  id="similar-cars-count"
                  type="number"
                  min={isEdit ? "0" : "1"}
                  max="50"
                  value={duplicateCount}
                  onChange={(e) => setDuplicateCount(Math.max(isEdit ? 0 : 1, Math.min(50, Number(e.target.value) || 0)))}
                  className="w-20 text-center font-bold tabular-nums text-lg h-9"
                  data-testid="similar-cars-count"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 rounded-lg"
                  onClick={() => setDuplicateCount((v) => Math.min(50, Number(v || 0) + 1))}
                  disabled={Number(duplicateCount) >= 50}
                  data-testid="similar-cars-increment"
                >+</Button>
              </div>
              <span className="text-xs text-neutral-500">Max 50</span>
            </div>
            {isEdit && Number(duplicateCount) >= 1 && (
              <div className="text-xs text-violet-900 bg-violet-100/60 rounded-lg px-3 py-2 mt-3" data-testid="similar-cars-preview">
                When you save, <strong>{duplicateCount}</strong> additional {Number(duplicateCount) === 1 ? "copy" : "copies"} of <strong>{form.make || "this car"} {form.model || ""}</strong> will be created.
                Clones are auto-suffixed
                (<code className="text-[11px] bg-white/80 px-1 rounded">{form.model || "Model"} #2</code>{Number(duplicateCount) > 1 ? <>…<code className="text-[11px] bg-white/80 px-1 rounded">{form.model || "Model"} #{Number(duplicateCount) + 1}</code></> : null})
                with placeholder plates <code className="text-[11px] bg-white/80 px-1 rounded">TEMP-2</code>{Number(duplicateCount) > 1 ? <>…<code className="text-[11px] bg-white/80 px-1 rounded">TEMP-{Number(duplicateCount) + 1}</code></> : null}. Edit them from My cars.
              </div>
            )}
            {!isEdit && Number(duplicateCount) > 1 && (
              <div className="text-xs text-violet-900 bg-violet-100/60 rounded-lg px-3 py-2 mt-3" data-testid="similar-cars-preview">
                <strong>{duplicateCount}</strong> copies of <strong>{form.make || "this car"} {form.model || ""}</strong> will be created.
                Clones {Number(duplicateCount) > 2 ? "2–" : ""}{duplicateCount} are auto-suffixed
                (<code className="text-[11px] bg-white/80 px-1 rounded">{form.model || "Model"} #2</code>…
                <code className="text-[11px] bg-white/80 px-1 rounded">{form.model || "Model"} #{duplicateCount}</code>)
                with placeholder plates <code className="text-[11px] bg-white/80 px-1 rounded">TEMP-2</code>…<code className="text-[11px] bg-white/80 px-1 rounded">TEMP-{duplicateCount}</code>. Edit them from My cars.
              </div>
            )}
          </div>
        </div>
      {existing?.id && (
        <div className="mt-4">
          <VendorFeatureRequestCard car={carShadow || existing} onChanged={refetchCarShadow} />
        </div>
      )}
      <Button onClick={submit} disabled={saving} className="rounded-full bg-blue-600 hover:bg-blue-700 w-full h-11" data-testid="submit-car-btn">
        {saving
          ? "Saving…"
          : isEdit
            ? (Number(duplicateCount) >= 1
                ? `Save & list ${duplicateCount} more`
                : "Save changes")
            : (Number(duplicateCount) > 1 ? `List ${duplicateCount} cars` : "List car")}
      </Button>
    </div>
  );
}


/**
 * Make picker. Curated list of ~35 EU-market makes + an "Other (type custom)" escape so
 * vendors with rare brands aren't blocked. Selecting "Other" reveals a free-text input;
 * the chosen value flows up to the parent as a plain string in either case so the rest
 * of the form (submit payload, server, agreement) doesn't need to know whether the value
 * came from the picklist or free-text. We detect "free-text mode" by checking whether
 * the current make is missing from the catalogue — that means it was either typed-custom
 * or persisted before this dropdown shipped.
 */
function MakePicker({ make, onChange }) {
  const isCustom = make && !CAR_MAKES.includes(make);
  const [mode, setMode] = useState(isCustom ? "other" : "picklist");
  const handleSelect = (v) => {
    if (v === OTHER_OPTION) {
      setMode("other");
      onChange("");
    } else {
      setMode("picklist");
      onChange(v);
    }
  };
  return (
    <div>
      <Label>Make</Label>
      {mode === "other" ? (
        <div className="mt-1 flex gap-2">
          <Input
            className="rounded-xl flex-1"
            value={make}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Type custom make"
            autoFocus
            data-testid="car-make-custom"
          />
          <Button type="button" variant="ghost" size="sm" className="rounded-full text-xs" onClick={() => { setMode("picklist"); onChange(""); }} data-testid="car-make-back">
            Back to list
          </Button>
        </div>
      ) : (
        <Select value={make || ""} onValueChange={handleSelect}>
          <SelectTrigger className="mt-1 rounded-xl" data-testid="car-make"><SelectValue placeholder="Pick a make" /></SelectTrigger>
          <SelectContent className="max-h-72">
            {CAR_MAKES.map((m) => <SelectItem key={m} value={m} data-testid={`car-make-${m}`}>{m}</SelectItem>)}
            <SelectItem value={OTHER_OPTION} data-testid="car-make-other">Other (type custom)…</SelectItem>
          </SelectContent>
        </Select>
      )}
    </div>
  );
}

/**
 * Model picker. Disabled until a Make is chosen. Filters models to the selected make's
 * catalogue (CAR_MODELS[make] or empty when "Other" was picked → forces free-text).
 * Same "Other" escape hatch as MakePicker for makes whose catalogue doesn't list a
 * specific trim/variant.
 */
function ModelPicker({ make, model, onChange }) {
  const models = make ? CAR_MODELS[make] || [] : [];
  const isCustom = model && (!make || !models.includes(model));
  // Force free-text whenever the make has no catalogue (custom make or empty catalogue).
  const forceCustom = make && models.length === 0;
  const [mode, setMode] = useState(isCustom || forceCustom ? "other" : "picklist");
  // Keep mode in sync when parent changes the make.
  useEffect(() => {
    if (forceCustom) setMode("other");
    else if (!model || models.includes(model)) setMode("picklist");
  }, [make, forceCustom, model, models]);
  const handleSelect = (v) => {
    if (v === OTHER_OPTION) {
      setMode("other");
      onChange("");
    } else {
      setMode("picklist");
      onChange(v);
    }
  };
  return (
    <div>
      <Label>Model</Label>
      {mode === "other" ? (
        <div className="mt-1 flex gap-2">
          <Input
            className="rounded-xl flex-1"
            value={model}
            onChange={(e) => onChange(e.target.value)}
            placeholder={make ? `Type ${make} model` : "Pick a make first"}
            disabled={!make}
            autoFocus={!!make}
            data-testid="car-model-custom"
          />
          {!forceCustom && (
            <Button type="button" variant="ghost" size="sm" className="rounded-full text-xs" onClick={() => { setMode("picklist"); onChange(""); }} data-testid="car-model-back">
              Back to list
            </Button>
          )}
        </div>
      ) : (
        <Select value={model || ""} onValueChange={handleSelect} disabled={!make}>
          <SelectTrigger className="mt-1 rounded-xl" data-testid="car-model"><SelectValue placeholder={make ? "Pick a model" : "Pick a make first"} /></SelectTrigger>
          <SelectContent className="max-h-72">
            {models.map((m) => <SelectItem key={m} value={m} data-testid={`car-model-${m}`}>{m}</SelectItem>)}
            <SelectItem value={OTHER_OPTION} data-testid="car-model-other">Other (type custom)…</SelectItem>
          </SelectContent>
        </Select>
      )}
    </div>
  );
}

/**
 * Mileage policy editor. Three states:
 *   - "" (not specified): nothing on the agreement, vendor will discuss with renter
 *   - "unlimited": no cap, displayed as a perk on the car page
 *   - "limited": daily km cap + extra-km rate; informational at booking time, vendor
 *      settles overage via the existing deposit-withhold flow at trip end.
 */
function MileagePolicyEditor({ policy, limit, rate, onChange }) {
  const set = (k, v) => onChange({ [k]: v });
  return (
    <div className="rounded-2xl border border-border p-4 bg-neutral-50/40" data-testid="mileage-policy-editor">
      <div className="flex items-center gap-2 mb-2">
        <Gauge className="h-4 w-4 text-neutral-500" />
        <Label>Mileage policy</Label>
      </div>
      <p className="text-xs text-neutral-500 mb-3">
        Set how many km the renter can drive per day. Overage is informational on the agreement; you settle it at trip-end via the existing deposit-withhold flow.
      </p>
      <div className="flex flex-wrap gap-2">
        {[
          { key: "", label: "Not specified" },
          { key: "unlimited", label: "Unlimited mileage" },
          { key: "limited", label: "Limited (set km/day)" },
        ].map((opt) => (
          <button
            key={opt.key || "unset"}
            type="button"
            onClick={() => set("mileage_policy", opt.key)}
            className={`text-xs px-3 py-1.5 rounded-full border transition ${policy === opt.key ? "bg-neutral-900 text-white border-neutral-900" : "border-border hover:border-neutral-400 text-neutral-600"}`}
            data-testid={`mileage-option-${opt.key || "unset"}`}
          >
            {opt.label}
          </button>
        ))}
      </div>
      {policy === "limited" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
          <div>
            <Label className="text-xs">Daily km limit</Label>
            <Input
              type="number"
              min="1"
              step="10"
              className="mt-1 rounded-xl"
              value={limit ?? ""}
              onChange={(e) => set("mileage_limit_km_per_day", e.target.value)}
              placeholder="e.g. 200"
              data-testid="mileage-limit-km"
            />
          </div>
          <div>
            <Label className="text-xs">Extra km rate (€/km)</Label>
            <Input
              type="number"
              min="0"
              step="0.05"
              className="mt-1 rounded-xl"
              value={rate ?? ""}
              onChange={(e) => set("mileage_extra_km_rate", e.target.value)}
              placeholder="e.g. 0.30"
              data-testid="mileage-extra-rate"
            />
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Custom checklist editor for vendors. Items entered here are appended (in order, below
 * the universal 10-item set) onto every pickup + return checklist generated for THIS
 * car's bookings. Useful for fleet-specific extras: surfboard racks, child seats,
 * detachable tow bar, etc. Empty strings are server-side filtered so a trailing empty
 * row doesn't pollute the saved list.
 */
function CustomChecklistEditor({ items, onChange }) {
  const safeItems = items || [];
  const addRow = () => onChange([...safeItems, ""]);
  const updateRow = (i, v) => onChange(safeItems.map((it, idx) => (idx === i ? v : it)));
  const removeRow = (i) => onChange(safeItems.filter((_, idx) => idx !== i));
  return (
    <div className="rounded-2xl border border-border p-4 bg-neutral-50/40" data-testid="custom-checklist-editor">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-sm font-semibold">Custom checklist rows</span>
        <span className="text-xs text-neutral-500">— appended to the universal 10-item pickup/return checklist</span>
      </div>
      <p className="text-xs text-neutral-500 mb-3">
        Examples: "Surfboard racks present", "Child seat installed", "Dashcam memory card returned".
      </p>
      <div className="space-y-2">
        {safeItems.map((row, i) => (
          <div key={i} className="flex gap-2 items-center">
            <Input
              value={row}
              onChange={(e) => updateRow(i, e.target.value)}
              placeholder={`Item ${i + 1}`}
              className="rounded-xl flex-1"
              data-testid={`custom-checklist-item-${i}`}
            />
            <Button type="button" variant="ghost" size="sm" onClick={() => removeRow(i)} className="rounded-full text-rose-600" data-testid={`custom-checklist-remove-${i}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
      </div>
      <Button type="button" variant="outline" size="sm" onClick={addRow} className="rounded-full mt-3" data-testid="custom-checklist-add">
        + Add row
      </Button>
    </div>
  );
}

